import { UserDoc, displayUser } from './types';

export function fmtDate(iso?: string, withTime = false): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const date = d.toLocaleDateString('es-AR');
  return withTime ? `${date} ${d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}` : date;
}

/**
 * Día calendario de una fecha que se cargó como fecha (deadline, inicio), en
 * formato 'yyyy-mm-dd'. '' si no hay o no parsea.
 *
 * Se lee en UTC a propósito: esas fechas se eligen con un <input type="date">,
 * viajan como 'yyyy-mm-dd' y la base las guarda como medianoche UTC de ese
 * día, así que el día UTC es exactamente el que tipeó quien cargó la tarea.
 * Leerlas en hora local (lo que hace toLocaleDateString) las corre un día para
 * atrás en cualquier huso al oeste de Greenwich, el nuestro incluido.
 */
export function diaFecha(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}

/** yyyy-mm-dd para inputs date a partir de un ISO. */
export function toDateInput(iso?: string): string {
  return diaFecha(iso);
}

/**
 * dd/mm/yyyy de una fecha que se cargó como fecha (deadline, inicio).
 *
 * No pasa por toLocaleDateString como fmtDate: esas fechas son un día, no un
 * instante. Si la base las guarda como medianoche UTC —el default de un
 * proyecto Supabase—, formatearlas en hora local las muestra un día antes en
 * cualquier huso al oeste de Greenwich. Formatear el día UTC devuelve la fecha
 * que se eligió, corra el servidor en el huso que corra.
 *
 * fmtDate se sigue usando para instantes reales (finished_at, created_at):
 * ahí sí corresponde la hora local de quien mira.
 */
export function fmtDia(iso?: string): string {
  const dia = diaFecha(iso);
  return dia ? dia.split('-').reverse().join('/') : '—';
}

/**
 * Día calendario local de un instante, en formato 'yyyy-mm-dd'.
 *
 * Al revés que diaFecha: la entrega y el "hoy" son momentos reales, y la
 * jornada que cuenta es la de quien trabaja. Una entrega a las 22:00 del día
 * del deadline pertenece a ese día laboral aunque en UTC ya sea el siguiente.
 */
export function diaLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

/** Igual que diaLocal pero desde un ISO; '' si no hay o no parsea. */
export function diaLocalDe(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : diaLocal(d);
}

/**
 * Días enteros entre dos días calendario 'yyyy-mm-dd' (negativo si `b` quedó
 * antes que `a`). null si alguno no es un día válido.
 *
 * Compara días y no instantes, así que no la afectan ni la hora ni el huso:
 * de un día al siguiente siempre es 1, aunque en el medio haya un cambio de
 * horario de verano.
 */
export function diasEntre(a: string, b: string): number | null {
  const ta = Date.parse(`${a}T00:00:00Z`);
  const tb = Date.parse(`${b}T00:00:00Z`);
  if (isNaN(ta) || isNaN(tb)) return null;
  return Math.round((tb - ta) / 86400000);
}

export function nameByEmail(email: string, users: UserDoc[]): string {
  if (!email) return '—';
  const u = users.find((x) => x.email.toLowerCase() === email.toLowerCase());
  return u ? displayUser(u) : email;
}

export function errMsg(e: any): string {
  return e?.message || e?.details || String(e) || 'Error inesperado.';
}

/** Recorta a `max` agregando elipsis. Para encabezados y celdas de tabla. */
export function truncate(s: string, max: number): string {
  const v = (s || '').trim();
  return v.length > max ? `${v.slice(0, max)}…` : v;
}

/**
 * Cómo se nombra una tarea en un encabezado: el título si lo tiene y, si no
 * (tareas anteriores al campo), la descripción recortada para que un párrafo
 * entero no termine funcionando de título.
 */
export function taskHeading(titulo: string, description: string, max = 70): string {
  return truncate(titulo, max) || truncate(description, max) || 'Tarea sin título';
}

/**
 * Clasificación corta de una tarea para bajadas y encabezados: categoría,
 * pieza y sede, las tres dimensiones que dicen "qué es" el pedido. Devuelve
 * '' si la tarea no tiene ninguna; cada vista decide su propio texto vacío.
 */
export function taskSummary(t: { categoria: string; pieza: string; sede: string }): string {
  return [t.categoria, t.pieza, t.sede].filter(Boolean).join(' · ');
}
